import React, { useRef, useState } from "react";
import { Menu, MenuItem, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CSVLoader } from "../CSVLoader";
import type { Data } from "../types";
import { useErrorNotification } from "@common/components/Notification";
import { CSV } from "@assets/icons";

export enum MenuItems {
  ListAsDisplayed = "List view as displayed",
  RawData = "Raw Data",
  ExtendedProperties = "Raw Data with Extended Properties",
  ExportSelected = "Export selected",
}

export type CSVMenuProps = {
  availableMenuItems?: MenuItems[];
  getData?: (item: MenuItems) => Promise<Data>;
  count?: number;
};

const CSVMenu = ({
  availableMenuItems: availableMenuItems = [
    MenuItems.ListAsDisplayed,
    MenuItems.RawData,
    MenuItems.ExtendedProperties,
  ],
  getData,
  count = 0,
}: CSVMenuProps) => {
  const dispatchError = useErrorNotification();

  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Data | undefined>(undefined);

  const ref = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(event.currentTarget !== null);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const onMenuItemClickInternal = (item: MenuItems) => async () => {
    handleClose();
    if (getData) {
      setLoading(true);
      try {
        const data = await getData(item);
        setData(data);
      } catch (error) {
        dispatchError(error);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title={t("Export") as string} arrow>
        <span>
          <IconButton size="small" onClick={handleClick} ref={ref}>
            <CSV />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={ref.current}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": "basic-button",
            sx: { paddingTop: 0 },
          },
        }}
      >
        <MenuItem
          disabled
          sx={(theme) => ({ backgroundColor: theme.palette.background.dark })}
        >
          {t("Export List to CSV")}
        </MenuItem>
        {count > 0 && (
          <MenuItem
            key={MenuItems.ExportSelected}
            onClick={onMenuItemClickInternal(MenuItems.ExportSelected)}
          >
            {t(MenuItems.ExportSelected)}
          </MenuItem>
        )}
        {availableMenuItems.map((i) => (
          <MenuItem key={i} onClick={onMenuItemClickInternal(i)}>
            {t(i)}
          </MenuItem>
        ))}
      </Menu>
      <CSVLoader
        loading={loading}
        data={data}
        resetData={() => setData(undefined)}
      />
    </>
  );
};

export default CSVMenu;
